import { type ReactNode, useEffect, useState } from 'react'

import type { ChangeFilterOptions } from '~/hooks/services/servicenow/queries/useChangesQuery'

import moment from 'moment'
import { useLocation } from 'react-router'

import { DEFAULT_FILTER_OPTIONS } from '../../constants'
import { ChangeFiltersContext } from './context'

export function ChangeFiltersProvider({ children }: { children: ReactNode }) {
  const location = useLocation()

  const [filterOptions, setFilterOptions] = useState<ChangeFilterOptions>(DEFAULT_FILTER_OPTIONS)
  const [dateRangeFromUrl, setDateRangeFromUrl] = useState<boolean>(false)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const stateParam = params.get('filter')?.toLowerCase()
    const dateRangeParam = params.get('dateRange')

    const initialFilterOptions = { ...DEFAULT_FILTER_OPTIONS }

    if (stateParam) {
      const capitalizedState = stateParam.charAt(0).toUpperCase() + stateParam.slice(1)
      initialFilterOptions.state = [capitalizedState]
    }

    // Handle date range parameter
    if (dateRangeParam) {
      // Handle date range with comma separator: 'YYYY-MM-DD,YYYY-MM-DD'
      if (dateRangeParam.includes(',')) {
        const [startDateStr, endDateStr] = dateRangeParam.split(',')
        const startDate = moment(startDateStr, 'YYYY-MM-DD')
        const endDate = moment(endDateStr, 'YYYY-MM-DD')

        if (startDate.isValid() && endDate.isValid()) {
          initialFilterOptions.startDate = startDate.startOf('day')
          initialFilterOptions.endDate = endDate.endOf('day')
          initialFilterOptions.period = 'custom'
          setDateRangeFromUrl(true)
        }
      } else {
        // Handle single date: 'YYYY-MM-DD' (set start and end to the same day)
        const singleDate = moment(dateRangeParam, 'YYYY-MM-DD')
        if (singleDate.isValid()) {
          initialFilterOptions.startDate = singleDate.startOf('day')
          initialFilterOptions.endDate = singleDate.endOf('day')
          initialFilterOptions.period = 'custom'
          setDateRangeFromUrl(true)
        }
      }
    }

    setFilterOptions(initialFilterOptions)
  }, [location.search])

  return (
    <ChangeFiltersContext.Provider value={{ filterOptions, setFilterOptions, dateRangeFromUrl, setDateRangeFromUrl }}>{children}</ChangeFiltersContext.Provider>
  )
}
